import { Headset as HeadsetRegular } from "lucide-react";
import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { SkyButton } from "@skypointcloud/skypoint-ui";
import { SkyProvider, createSkyTheme } from "@skypointcloud/skypoint-ui";
import "@skypointcloud/skypoint-ui/styles.css";

import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { createMsalInstance } from "./config/msalConfig";
import { authService as auth, setMsalInstance } from "./utils/authService";
import SkypointLogoLoader from "./components/SkypointLogoLander/SkypointLogoLoader";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginHandler } from "./components/auth/LoginHandler";
import { useB2CPolicies } from "./hooks/useB2CPolicies";
import SkyPointLogin from "./components/skypointlogin";
import { ROUTES } from "./components/shared/constant";
import VerifyEmail from "./components/verifyemail";
import Callback from "./components/callback";
import Signup from "./components/signup";
import { store, persistor } from "./store";
import "./index.css";
import AppNew from "./AppNew";
import AccountLocked from "./components/icons/accountLocked";
import { Styles } from "./styles/footerStyles";
import { MessageBlock } from "./components/MessageBlock";
import { ConfirmationProvider } from "./components/shared/confirm";
import { getAndClearOAuthReturnPath } from "./utils/oauthReturnHandler";
import { checkAndClearOnVersionChange } from "./utils/versionCheck";
import { installRadixPopoverScrollClose } from "./utils/radixPopoverScrollClose";
import { installRadixScrollShim } from "./utils/radixScrollShim";

// Check for version changes and clear stale caches BEFORE any other initialization
// This prevents issues with stale tokens and chunk references after deployments
checkAndClearOnVersionChange();

// Log Valkey cache connection status at app boot so it's visible in the
// browser console. Temporary — remove once Valkey is fully bedded in.
fetch("/health?quick=true")
  .then((r) => r.json())
  .then((d) => {
    const status = d?.dependencies?.valkey?.status ?? "unknown";
    const color = status === "healthy" ? "#16a34a" : "#ea580c";
    console.log(`%c[Valkey] ${status}`, `color: ${color}; font-weight: bold`, d?.dependencies);
  })
  .catch((err) => console.warn("[Valkey] health check failed", err));

// Close Radix popovers when an ancestor container scrolls — fixes "flying"
// dropdowns in drawers/dialogs where Radix's own position tracking can't see
// the nested scroll.
installRadixPopoverScrollClose();

// Restore mouse-wheel scrolling inside Radix popover listboxes (country
// selector, long dropdowns) when opened from inside a modal dialog/drawer.
// Only active while a dialog is open — plain-page dropdowns are untouched.
installRadixScrollShim();

const SkypointSignUp = lazy(() => import("./components/skypointsignup"));
const PublicSurveyPage = lazy(() => import("./views/surveys/public/PublicSurveyPage"));
const PublicAssessmentPage = lazy(() => import("./views/assessments/public/PublicAssessmentPage"));
const PublicRsvpPage = lazy(() => import("./views/crm/publicRsvp/PublicRsvpPage"));

/**
 * ⚠️ THE BRAND COLOUR MUST BE PASSED. `createSkyTheme()` with no argument falls
 * back to the library's own default brand (#107C10, a green), which is what was
 * rendering a TEAL "Cancel" button in a product whose accent is amber. Every
 * Sky* component that paints from the brand ramp -- secondary buttons, toggles,
 * active rows, focus rings -- inherited that default.
 *
 * #ffb31c is the design system's `--brand-primary` / `--accent`, quoted from
 * its tokens.css rather than picked to match a screenshot.
 */
// A literal, NOT var(--color-brand-80): createSkyTheme parses this value in
// JS to derive the brand ramp, so a CSS custom property is not a colour to
// it and it throws "Invalid brand color" at startup.
const skyTheme = createSkyTheme({ brandColor: "#ffb31c" });

interface IUserExistsResponse {
  isSuccessful: boolean;
  message: string;
}

declare global {
  interface Window {
    instanceCultureName: string;
    instanceTimezone: string;
    allowEdit: any;
    allowDelete: any;
    allowView: any;
    isFullAccess: boolean;
    isPlatformAdmin: boolean;
    isTenantAdmin: boolean;
    isInstanceAdmin: boolean;
    Stripe: any;
  }
}

const getUser = async () => {
  const user = auth.currentUser();
  if (user) {
    const userExistsRequest = { objectId: user.id };

    const response = await fetch("/api/user/checkUserExists", {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userExistsRequest),
    });

    if (!response.ok) throw new Error(response.statusText);

    return response.json();
  }

  throw new Error("User not logged-in");
};

const ApplicationBlock = () => {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState<IUserExistsResponse>();
  const [error, setError] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only fetch user data if we have accounts
    if (accounts && accounts.length > 0) {
      getUser().then(
        (data) => {
          setData(data);
          setError(null);
          setIsLoading(false);
        },
        (error) => {
          console.log(error);
          setError(error);
          setIsLoading(false);
        },
      );
    } else {
      setIsLoading(false);
    }
  }, [accounts]);

  if (isLoading) {
    return <SkypointLogoLoader />;
  } else if (error) {
    return <MessageBlock>Something went wrong: {error.message}</MessageBlock>;
  } else if (!data || !data.isSuccessful) {
    return (
      <MessageBlock>
        <AccountLocked />
        <h3>Your account is locked. Contact support.</h3>
        <h4>
          Click here to{" "}
          <a
            href="#"
            onClick={() => instance.logoutRedirect()}
          >
            Sign out
          </a>
        </h4>
        <a href="https://skypointcloud.com/customer-support/">
          <SkyButton
            appearance="primary"
            icon={HeadsetRegular}
            style={Styles.needHelpButton.root as any}
          >
            Need help?
          </SkyButton>
        </a>
      </MessageBlock>
    );
  } else {
    // Check if account is enabled using idTokenClaims
    const currentUser = auth?.currentUser();
    const isAccountEnabled = currentUser?.isAccountEnabled !== false;

    if (!isAccountEnabled) {
      return (
        <MessageBlock>
          <h3>
            Your account has been locked. Contact your support person to unlock it, then try again.
          </h3>
          <h4>
            Click here to{" "}
            <a
              href="#"
              onClick={() => instance.logoutRedirect()}
            >
              Sign out
            </a>
          </h4>
        </MessageBlock>
      );
    }

    return (
      <BrowserRouter>
        <Routes>
          <Route
            path={ROUTES.CALLBACK}
            element={<Callback />}
          />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <AppNew />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    );
  }
};

/**
 * Authenticated App Wrapper
 * Uses MSAL React templates for authentication state management
 */
const AuthenticatedApp = () => {
  const { inProgress } = useMsal();
  const [oauthReturnHandled, setOauthReturnHandled] = useState(false);

  // Phase 7: Handle B2C policies (password reset, error codes, etc.)
  useB2CPolicies();

  useEffect(() => {
    if (inProgress === "none" && !oauthReturnHandled) {
      const result = getAndClearOAuthReturnPath();
      if (result && window.location.pathname !== result.path.split("?")[0]) {
        // Restore the launch location; the callback status rides ?integration&status on
        // the destination page's own reader (MarketingIntegrationsPage), not here.
        window.location.replace(result.path);
      } else {
        setOauthReturnHandled(true);
      }
    }
  }, [inProgress, oauthReturnHandled]);

  // Show loader while MSAL is processing interactions
  if (inProgress === "startup" || inProgress === "handleRedirect") {
    return <SkypointLogoLoader />;
  }

  // Show loader while handling OAuth return
  if (!oauthReturnHandled) {
    return <SkypointLogoLoader />;
  }

  return (
    <>
      {/* Show ApplicationBlock when user is authenticated */}
      <AuthenticatedTemplate>
        <ApplicationBlock />
      </AuthenticatedTemplate>

      {/* Trigger login and show loader when user is not authenticated */}
      <UnauthenticatedTemplate>
        <LoginHandler />
      </UnauthenticatedTemplate>
    </>
  );
};

/**
 * App initialization wrapper
 * Handles MSAL instance creation and initialization
 */
// Lightweight loading spinner for public pages
const SimpleLoader = () => (
  <div
    className="flex justify-center items-center"
    style={{ height: "100vh" }}
  >
    <div
      className="w-[40px] h-[40px] rounded-full border-t-3 border-fg-link"
      style={{
        border: "3px solid var(--color-stroke-faint)",
        animation: "spin 1s linear infinite",
      }}
    />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

const AppWithMsal = ({ msalInstance }: { msalInstance: PublicClientApplication }) => {
  const url = window.location.href;

  // Public survey route - minimal setup, no PersistGate needed
  if (url.includes("/survey/")) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/survey/:instanceId/:token"
                  element={<PublicSurveyPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Public assessment route - minimal setup, no auth needed
  if (url.includes("/assessment/")) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/assessment/:instanceId/:token"
                  element={<PublicAssessmentPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Public community-event RSVP — minimal setup, no auth needed.
  //
  // ⚠️ Matched on the PATHNAME PREFIX, not `url.includes("/rsvp/")`. `url` is
  // the whole href, so an `includes` test also matches the string appearing in a
  // QUERY PARAMETER or a hash — `/crm/prospects?next=/rsvp/x` would have
  // rendered the unauthenticated page in place of the real one. The routes above
  // predate this and still use `includes`; this one does not copy the bug.
  if (window.location.pathname.startsWith("/rsvp/")) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/rsvp/:instanceId/:token/:response?"
                  element={<PublicRsvpPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Other public routes that don't require authentication
  if (
    url.includes("/signup") ||
    url.includes("/verifyemail") ||
    url.includes("/skypointlogin") ||
    url.includes("/skypointsignup")
  ) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <ConfirmationProvider>
          <Provider store={store}>
            <PersistGate
              loading={<SkypointLogoLoader />}
              persistor={persistor}
            >
              <BrowserRouter>
                <div className="relative h-full overflow-x-visible overflow-y-visible">
                  <Suspense fallback={<SkypointLogoLoader />}>
                    <Routes>
                      <Route
                        path="/signup"
                        element={<Signup />}
                      />
                      <Route
                        path="/verifyemail"
                        element={<VerifyEmail />}
                      />
                      <Route
                        path="/skypointlogin"
                        element={<SkyPointLogin />}
                      />
                      <Route
                        path="/skypointsignup"
                        element={<SkypointSignUp />}
                      />
                    </Routes>
                  </Suspense>
                </div>
              </BrowserRouter>
            </PersistGate>
          </Provider>
        </ConfirmationProvider>
      </SkyProvider>
    );
  }

  // Protected routes wrapped with MsalProvider
  return (
    <SkyProvider
      theme={skyTheme}
      defaultMode="light"
      toastConfig={{
        position: "top-center",
        maxToasts: 5,
        defaultDuration: 5000,
      }}
    >
      <ConfirmationProvider>
        <Provider store={store}>
          <PersistGate
            loading={<SkypointLogoLoader />}
            persistor={persistor}
          >
            <MsalProvider instance={msalInstance}>
              <AuthenticatedApp />
            </MsalProvider>
          </PersistGate>
        </Provider>
      </ConfirmationProvider>
    </SkyProvider>
  );
};

/**
 * Root component with async MSAL initialization
 */
const AppRoot = () => {
  const [msalInstance, setMsalInstanceState] = useState<PublicClientApplication | null>(null);
  const [initError, setInitError] = useState<Error | null>(null);

  useEffect(() => {
    // Initialize MSAL instance (v4 requires async initialization)
    createMsalInstance()
      .then((instance) => {
        // Set instance for auth service
        setMsalInstance(instance);
        setMsalInstanceState(instance);
      })
      .catch((error) => {
        console.error("Failed to initialize MSAL:", error);
        setInitError(error);
      });
  }, []);

  if (initError) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <MessageBlock>
          <h3>Authentication initialization failed</h3>
          <p>Please refresh the page. If the problem persists, contact support.</p>
        </MessageBlock>
      </SkyProvider>
    );
  }

  if (!msalInstance) {
    return (
      <SkyProvider
        theme={skyTheme}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <SkypointLogoLoader />
      </SkyProvider>
    );
  }

  return <AppWithMsal msalInstance={msalInstance} />;
};

createRoot(document.getElementById("root")!).render(<AppRoot />);
